import { useMemo } from 'react'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { createBrowserRouter } from 'react-router'
import { RouterProvider } from 'react-router/dom'

import { AppRoot } from './routes/app/root'

import { ErrorComponent } from '@/components/error-component'
import { HydrateFallback } from '@/components/hydrate-fallback'
import { paths } from '@/config/path'
import { OnboardingGuard } from '@/features/tech-onboarding/guard'
import { ProtectedRoute, RoleGuard } from '@/lib/auth'

const createRouter = (queryClient: QueryClient) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const convert = (queryClient: QueryClient) => (m: any) => {
    const { clientLoader, clientAction, default: Component, ...rest } = m
    return {
      ...rest,
      loader: clientLoader?.(queryClient),
      action: clientAction?.(queryClient),
      Component,
    }
  }

  return createBrowserRouter([
    /*
     * =====================
     * =====================
     * PUBLIC ROUTES
     * =====================
     * =====================
     */
    {
      path: paths.home.path,
      lazy: () => import('./routes/landing').then(convert(queryClient)),
      hydrateFallbackElement: <HydrateFallback />,
    },
    {
      path: paths.auth.register.path,
      lazy: () => import('./routes/auth/register').then(convert(queryClient)),
      hydrateFallbackElement: <HydrateFallback />,
    },
    {
      path: paths.auth.login.path,
      lazy: () => import('./routes/auth/login').then(convert(queryClient)),
      hydrateFallbackElement: <HydrateFallback />,
    },
    {
      path: paths.auth['password-reset'].path,
      lazy: () => import('./routes/auth/reset-password').then(convert(queryClient)),
      hydrateFallbackElement: <HydrateFallback />,
    },
    {
      path: paths.technician.profile.path,
      lazy: () => import('./routes/profile-page/tech-profile').then(convert(queryClient)),
      errorElement: <div className="p-4 text-center">Profil non trouvé</div>,
      hydrateFallbackElement: <HydrateFallback />,
    },
    /**
     * PROTECTED OUTER ROUTES
     */
    {
      path: paths.auth['update-password'].path,
      lazy: () => import('./routes/auth/update-password').then(convert(queryClient)),
      hydrateFallbackElement: <HydrateFallback />,
    },
    {
      path: paths.onboarding.path,
      lazy: async () => {
        const { OnboardingRoute } = await import('./routes/app/onboarding')
        return {
          element: (
            <ProtectedRoute>
              <OnboardingRoute />
            </ProtectedRoute>
          ),
        }
      },
      ErrorBoundary: ErrorComponent,
      hydrateFallbackElement: <HydrateFallback />,
    },
    /*
     * =====================
     * =====================
     * PROTECTED ROUTES
     * =====================
     * =====================
     */
    {
      path: paths.app.root.path,
      element: (
        <ProtectedRoute>
          <OnboardingGuard>
            <AppRoot />
          </OnboardingGuard>
        </ProtectedRoute>
      ),
      ErrorBoundary: ErrorComponent,
      hydrateFallbackElement: <HydrateFallback />,
      children: [
        {
          path: paths.app.orders.path,
          lazy: () => import('./routes/app/orders/orders').then(convert(queryClient)),
        },
        {
          path: paths.app.order.getHref(':orderId'),
          lazy: () => import('./routes/app/orders/order').then(convert(queryClient)),
        },
        {
          path: paths.app.settings.path,
          lazy: () => import('./routes/app/settings/settings').then(convert(queryClient)),
        },
        {
          element: <RoleGuard requiredRole="practician" />,
          children: [
            {
              path: paths.app['new-order'].getHref(':serviceId'),
              lazy: () => import('./routes/app/orders/new-order/new-order').then(convert(queryClient)),
            },
            {
              path: paths.app['my-patients'].path,
              lazy: () => import('./routes/app/my-patients/my-patients').then(convert(queryClient)),
            },
            {
              path: paths.app['search-service'].path,
              lazy: () => import('./routes/app/search-service/search-service').then(convert(queryClient)),
            },
          ],
        },
        {
          element: <RoleGuard requiredRole="technician" />,
          children: [
            {
              path: paths.app.services.path,
              lazy: () => import('./routes/app/services/services').then(convert(queryClient)),
            },
            {
              path: paths.app.service.path,
              lazy: () => import('./routes/app/services/service').then(convert(queryClient)),
            },
            {
              path: paths.app['create-service'].path,
              lazy: () => import('./routes/app/services/create-service').then(convert(queryClient)),
            },
            {
              path: paths.app['service-questions'].path,
              lazy: () => import('./routes/app/services/questions').then(convert(queryClient)),
            },
            {
              path: paths.app['new-order-solo'].path,
              lazy: () => import('./routes/app/orders/new-order/new-order-solo').then(convert(queryClient)),
            },
          ],
        },
      ],
    },
    {
      path: '*',
      lazy: async () => {
        const { NotFoundRoute } = await import('../components/not-found')
        return { Component: NotFoundRoute }
      },
      ErrorBoundary: ErrorComponent,
      hydrateFallbackElement: <HydrateFallback />,
    },
  ])
}

export const AppRouter = () => {
  const queryClient = useQueryClient()
  const router = useMemo(() => createRouter(queryClient), [queryClient])
  // const router = createRouter(queryClient)

  // const origNavigate = router.navigate;
  // console.log('Router Initial State', {
  //   basename: router.basename,
  //   state: router.state,
  //   routes: router.routes,
  // });

  // router.navigate = (...args) => {
  //   console.log('Navigate', {
  //     args,
  //     currentLocation: router.state.location,
  //     currentAction: router.state.navigation.formAction,
  //   });
  //   return origNavigate.apply(router, args);
  // };

  // router.subscribe((state) => {
  //   console.log('Router State Updated', {
  //     location: state.location,
  //     action: state.navigation.formAction,
  //     state: state.navigation.state,
  //     error: state.errors,
  //   });
  // });

  return <RouterProvider router={router} />
}
